import { useEffect, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { cachedClientPickerOptions, fetchClientPickerOptions, type ClientPickerRow } from '../api/clients';

export interface ClientPickerOptions {
  clients: ClientPickerRow[];
  clientsLoading: boolean;
  /** For callers that edit a client and want the picker to reflect it right away. */
  setClients: Dispatch<SetStateAction<ClientPickerRow[]>>;
}

/**
 * Active clients for a company picker. Two things matter here: the list loads on
 * mount rather than when the picker is revealed, so the user is not waiting on a
 * request they triggered, and it starts from the shared cache, so opening a
 * second modal costs nothing. The api layer dedupes the request itself, so every
 * picker on a page can call this freely.
 */
export function useClientPickerOptions(): ClientPickerOptions {
  const cached = cachedClientPickerOptions();
  const [clients, setClients] = useState<ClientPickerRow[]>(cached ?? []);
  const [clientsLoading, setClientsLoading] = useState(cached == null);

  useEffect(() => {
    let cancelled = false;
    fetchClientPickerOptions()
      .then((list) => {
        if (!cancelled) setClients(list);
      })
      .catch(() => {
        if (!cancelled) setClients([]);
      })
      .finally(() => {
        if (!cancelled) setClientsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { clients, clientsLoading, setClients };
}
